/**
 * CORS Proxy utility for making API calls through nginx proxy
 * Uses cors-anywhere style URL format: proxyBase/originalUrl
 * Can be disabled via VITE_ENABLE_CORS_PROXY=false for local development
 *
 * MEWS calls always go through the backend proxy when VITE_BACKEND_API_URL is
 * configured (see USE_BACKEND_PROXY below), so no MEWS credentials live in the
 * frontend. This used to name a VITE_USE_MEWS_BACKEND_PROXY switch that no
 * code reads and no env file sets.
 */

// Virtual MEWS API URL — never called directly, only used to detect MEWS calls
// and extract the endpoint path for backend proxy routing.
// This must match the constant used in all page files that construct MEWS URLs.
import { getUpstreamJwt } from './jwtToken'
import { MEWS_CONNECTOR_BASE } from '@/utils/mewsConnector'
import { mewsProxyBasePath } from '@/composables/mewsProxyBasePath'

const MEWS_API_URL = MEWS_CONNECTOR_BASE
const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL || ''
// Backend proxy is always on when BACKEND_API_URL is configured. The
// user's upstream JWT is attached per request via getUpstreamJwt().
const USE_BACKEND_PROXY = !!BACKEND_API_URL

/**
 * Route MEWS API calls through the backend proxy.
 * Backend URL: POST /api/mews/{mewsPath}
 * Body is passed through as-is (minus auth tokens). Backend adds MEWS credentials.
 */
function getMewsEndpoint(url: string): string | null {
  if (!MEWS_API_URL || !url.startsWith(MEWS_API_URL)) return null
  return url.slice(MEWS_API_URL.length)
}

async function backendMewsProxy(mewsPath: string, options: RequestInit): Promise<any> {
  // Strip MEWS auth fields from body — backend adds them
  let body: Record<string, any> = {}
  if (options.body) {
    try {
      const parsed = JSON.parse(options.body as string)
      const { AccessToken: _a, ClientToken: _c, Client: _cl, ...cleanBody } = parsed
      body = cleanBody
    } catch { /* empty body is fine */ }
  }

  // /api/mews on a MEWS backend (unchanged); /api/pms/compat on a native one,
  // which answers the same endpoints in MEWS shape — see usePmsInfo.ts.
  const url = `${BACKEND_API_URL}${await mewsProxyBasePath()}${mewsPath}`
  const token = await getUpstreamJwt()

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Backend MEWS proxy error ${response.status}: ${errorText}`)
  }
  return response.json()
}

export async function proxy(url: string, options: RequestInit = {}): Promise<any> {
  try {
    // Route MEWS calls through backend proxy
    if (USE_BACKEND_PROXY) {
      const mewsPath = getMewsEndpoint(url)
      if (mewsPath) {
        return await backendMewsProxy(mewsPath, options)
      }
    }

    // Original CORS proxy logic
    const corsProxyEnabled = import.meta.env.VITE_ENABLE_CORS_PROXY === 'true'
    let targetUrl = url

    if (corsProxyEnabled) {
      const proxyBaseUrl = import.meta.env.VITE_CORS_PROXY_BASE_URL || 'https://board1.skyant.solactivy.com/api/proxy'
      targetUrl = `${proxyBaseUrl}/${url}`
    }

    const response = await fetch(targetUrl, options)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ Request failed:', response.status, errorText)
      throw new Error(`Request failed: ${response.status} ${errorText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('❌ Request error:', error)
    throw error
  }
}

export default {
  proxy
}
